<template>
  <div class="flex h-full flex-col border-r border-r-gray-300 bg-gray-100/60 text-charcoal shadow-lg dark:border-r-charcoal-700 dark:bg-charcoal-900 dark:text-gray-100 dark:shadow-none">

    <header v-if="showHeader" class="border-b border-gray-200 bg-white/70 py-2 px-4 backdrop-blur-sm dark:border-charcoal-700 dark:bg-charcoal-900/95">
      <div class="flex justify-between items-center">

        <div>
          <u-button
            v-if="showClearFiltersAction && effectiveAreFiltersApplied"
            label="Clear Filters"
            variant="link"
            color="black"
            size="sm"
            @click="clear"
            :padded="false"
            :ui="{font: 'font-medium'}"
            class="dark:text-gray-100"
          >
            <template #trailing>
              <u-chip color="charcoal" size="2xl" class="ml-2" :text="effectiveFilterCount" />
            </template>
          </u-button>
          <h3 v-else class="flex items-center gap-2">
            <u-icon name="i-ri-equalizer-line" class="text-xl text-charcoal-400 dark:text-gray-300" />
            <span class="leading-none font-semibold mt-px">Filters</span>
          </h3>
        </div>
        <u-button
          icon="i-ri-contract-left-line"
          variant="ghost"
          color="gray"
          size="xs"
          @click="emit('toggle')"
          :ui="{icon: {base: 'text-charcoal/80 dark:text-gray-300 scale-125'}}"
        />
      </div>
    </header>

    <section class="scrollbar-light dark:scrollbar-dark flex-1 overflow-y-auto p-2 sm:p-4 !pb-0">

      <div class="relative">
        <transition name="f-fade">
          <div class="absolute inset-0 z-40 bg-white/40 dark:bg-charcoal-900/60" v-if="isClearing || isLoadingFiles || isBuffering" />
        </transition>

        <div class="mb-3 flex cursor-pointer select-none items-center gap-2 rounded border border-charcoal-300/50 bg-white p-3 dark:border-charcoal-600 dark:bg-charcoal-800 dark:hover:bg-charcoal-700/80" v-if="isContextOwner" @click="favoriteActive = !favoriteActive">
          <u-icon class="text-2xl" :class="favoriteActive ? 'text-red-400' : 'text-charcoal-300 dark:text-gray-400'" :name="favoriteActive ? COMMON_ICONS.favoriteFill : COMMON_ICONS.favoriteLine" />
          <span class="font-semibold text-base flex-1 mt-px">Favorites</span>
          <u-checkbox v-model="favoriteActive" @click.stop />
        </div>

        <u-accordion
          :ui="accordionConfig"
          :items="filterPanelItems"
          color="charcoal"
          variant="ghost"
          size="xl"
          class="my-3"
        >

          <template #default="{item, open}">
            <button class="flex items-center gap-2 p-3 font-semibold text-charcoal transition-colors hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-charcoal-700/70">

              <u-icon v-if="item.icon" :name="item.icon" class="text-xl text-charcoal-300 dark:text-gray-400" />
              <span class="text-nowrap leading-none mt-px">{{item.label}}</span>

              <span class="flex-1 flex justify-end gap-1 flex-wrap">

              <template v-if="props.condensed && !open && hasCondensedIndicator(item.slot)">
                <span class="mt-px h-2.5 w-2.5 rounded-full bg-blue-500 shadow-[0_0_0_3px_rgba(59,130,246,0.18)]" />
              </template>

              <template v-else-if="!open && item.slot === 'sort-by'">
                <u-badge color="blue" size="xs" :label="order?.label" />
              </template>

              <template v-else-if="!open && item.slot === 'asset-type'">
                <u-badge
                  v-for="type in visibleFileTypes.filter(t => t.active)"
                  :key="type.key"
                  color="blue"
                  size="xs"
                  :label="type.label"
                />
              </template>

              <template v-else-if="!open && item.slot === 'date-range'">
                <u-badge v-if="dateRangeLabel" color="blue" size="xs">
                  {{dateRangeLabel}}
                </u-badge>
              </template>

              <template v-else-if="!open && item.slot === 'people' && people.length">
                <u-tooltip :text="people.map(p => p.name).join(', ')" :popper="{placement: 'top'}">
                  <u-badge color="blue" size="xs" :ui="{base: 'text-nowrap'}">{{people.length}} Selected</u-badge>
                </u-tooltip>
              </template>

              <template v-else-if="!open && item.slot === 'albums' && albums.length">
                <u-tooltip :text="albums.map(a => a.name).join(', ')" :popper="{placement: 'top'}">
                  <u-badge color="blue" size="xs" :ui="{base: 'text-nowrap'}">{{albums.length}} Selected</u-badge>
                </u-tooltip>
              </template>

              <template v-else-if="!open && item.slot === 'tags' && tags.length">
                <u-tooltip :text="tags.map(t => t.name).join(', ')" :popper="{placement: 'top'}">
                  <u-badge color="blue" size="xs" :ui="{base: 'text-nowrap'}">{{tags.length}} Selected</u-badge>
                </u-tooltip>
              </template>

            </span>

              <u-icon name="i-ri-arrow-down-s-line" class="text-xl text-charcoal-400 transition-all dark:text-gray-300" :class="open && '-rotate-180'" />
            </button>
          </template>

          <template #sort-by>
            <div class="grid grid-cols-2 gap-3">
              <template v-for="option in sortOptions" :key="option.paramName">
                <u-button
                  :label="option.label"
                  :color="option.paramName === order?.paramName ? 'charcoal' : 'gray'"
                  variant="solid"
                  size="lg"
                  block
                  @click="setSort(option)"
                />
              </template>

              <core-tooltip v-if="shouldShowCustomSortEdit" :text="customSortLimitMessage" :prevent="canEditCustomSort">
                <u-button
                  variant="link"
                  block
                  class="underline"
                  :disabled="!canEditCustomSort"
                  label="Edit Custom Order"
                  @click="openCustomSorter"
                />
              </core-tooltip>
            </div>
          </template>


          <template #asset-type>
            <div class="grid grid-cols-3 gap-3">
              <template v-for="type in visibleFileTypes" :key="type.key">
                <u-button
                  :label="type.label"
                  :color="type.active ? 'charcoal' : 'gray'"
                  variant="solid"
                  size="lg"
                  block
                  @click="type.active = !type.active"
                />
              </template>
            </div>
          </template>

          <template #date-range>
            <core-date-range-picker
              v-model="dateRange"
              class="text-charcoal dark:text-gray-100"
              trigger-size="lg"
              allow-custom
              overlay
              :popper="{ arrow: true, placement: screenMd ? 'right' : 'bottom'}"
              :ui="{
                background: 'bg-white dark:bg-charcoal-800',
                arrow: {
                  background: 'before:bg-white dark:before:bg-charcoal-800',
                }
              }"
            />
          </template>

          <template #people>
            <people-select-menu v-model="people" disable-create />
          </template>

          <template #tags>
            <tag-select-menu v-model="tags" disable-create />
          </template>

          <template #albums>
            <album-select-menu v-model="albums" disable-create />
          </template>

        </u-accordion>

        <div
          class="grid gap-3 my-3 rounded"
          :class="visibleOrganizationTypes.length === 1 ? 'grid-cols-1' : 'grid-cols-2'"
          v-if="shouldShowOrganization"
        >
          <template v-for="type in visibleOrganizationTypes" :key="type.key">
            <u-button
              @click="type.active = !type.active"
              :label="type.label"
              variant="solid"
              :color="type.active ? 'charcoal' : 'gray'"
              size="xl"
              block
            />
          </template>
        </div>
      </div>

      <footer class="sticky bottom-0 bg-gray-100/50 pb-4 dark:bg-charcoal-900/85">
        <u-button
          :label="submitButtonText"
          size="xl"
          block
          :loading="isClearing || isLoadingFiles"
          :disabled="filesCountFallback === 0"
          @click="emit('toggle')"
          class="shadow shadow-gray-300 dark:shadow-none"
        />
      </footer>
    </section>
  </div>
</template>

<script setup>
  import {storeToRefs} from 'pinia';
  import isEqual from 'lodash.isequal';
  import {makeFilesStore} from "~/stores/files.js";
  import {useScreenSize} from '~/composables/core/screen-size.js';
  import {getFileFilterPolicy} from '~/utils/file-filter-policy.js';

  const props = defineProps({
    filesStoreId: String,
    contextType: String,
    context: Object,
    // Restricts the Media Type filter to these file types; undefined = all types (still subject to
    // the context's filter policy). With 1 or fewer types there is nothing to choose, so the Media
    // Type section is removed and the remaining type (if any) is applied implicitly.
    allowedFileTypes: {
      type: Array,
      default: undefined
    },
    hideDateFilter: Boolean,
    hideClearFiltersAction: Boolean,
    hideHeader: Boolean,
    condensed: Boolean,
    defaultSubmitLabel: {
      type: String,
      default: 'Show All Files'
    }
  });

  const storeFiltersState = inject('storeFiltersState', false);

  const emit = defineEmits(['toggle']);

  const albumsStore = useAlbumsStore();
  const filesStore = props.filesStoreId
    ? makeFilesStore(props.filesStoreId)()
    : useFilesStore();

  const {collectionDescriptor, filesCountFallback, isLoadingFiles, isBuffering} = storeToRefs(filesStore);

  const filterPolicy = computed(() => getFileFilterPolicy({
    contextType: collectionDescriptor.value?.contextType || props.contextType,
    context: collectionDescriptor.value?.context || props.context
  }));
  const isContextOwner = computed(() => filterPolicy.value.canUseOwnerFilters);

  const showClearFiltersAction = computed(() => !props.hideClearFiltersAction);
  const showHeader = computed(() => !props.hideHeader);

  // `visibleFileTypes`/`shouldShowFileTypeFilter` are declared further down - fine, since this getter
  // isn't evaluated until after setup() finishes. A hidden Media Type section means any descriptor
  // file types are implicit, not something the customer applied.
  const activeDescriptorFileTypes = computed(() => {
    if (!shouldShowFileTypeFilter.value) {
      return [];
    }

    const visibleKeys = visibleFileTypes.value.map(type => type.key);

    return (collectionDescriptor.value?.fileTypes || []).filter(type => visibleKeys.includes(type));
  });

  const effectiveAreFiltersApplied = computed(() => Boolean(
    (isContextOwner.value && collectionDescriptor.value?.albums?.length) ||
    (isContextOwner.value && collectionDescriptor.value?.tags?.length) ||
    (isContextOwner.value && collectionDescriptor.value?.people?.length) ||
    activeDescriptorFileTypes.value.length ||
    collectionDescriptor.value?.organization?.length ||
    (filterPolicy.value.canUseDateFilter && collectionDescriptor.value?.dateRange?.val)
  ));

  const effectiveFilterCount = computed(() => {
    let count = 0;

    if (isContextOwner.value && collectionDescriptor.value?.albums?.length) {
      count += collectionDescriptor.value.albums.length;
    }

    if (isContextOwner.value && collectionDescriptor.value?.tags?.length) {
      count += collectionDescriptor.value.tags.length;
    }

    if (isContextOwner.value && collectionDescriptor.value?.people?.length) {
      count += collectionDescriptor.value.people.length;
    }

    count += activeDescriptorFileTypes.value.length;

    if (collectionDescriptor.value?.organization?.length) {
      count += collectionDescriptor.value.organization.length;
    }

    if (filterPolicy.value.canUseDateFilter && collectionDescriptor.value?.dateRange?.val) {
      count += 1;
    }

    return count;
  });

  const shouldShowCustomSort = computed(() => collectionDescriptor.value?.contextType === FILE_CONTEXTS.album);
  const sortOptions = computed(() => (
    filterPolicy.value.allowedSortParams
      ? FILE_SORT_OPTIONS.filter(option => filterPolicy.value.allowedSortParams.includes(option.paramName))
      : (shouldShowCustomSort.value ? FILE_SORT_OPTIONS_WITH_CUSTOM : FILE_SORT_OPTIONS)
  ));
  const hasCustomSort = computed(() => sortOptions.value.find(option => option.paramName === 'custom'));
  const albumFileCount = computed(() => collectionDescriptor.value?.context?.files_count || 0);
  const hasTooManyFilesForCustomSort = computed(() => albumFileCount.value > ALBUM_CUSTOM_ORDER_FILES_COUNT_LIMIT);
  const customSortLimitMessage = `Custom ordering is available for albums with ${ALBUM_CUSTOM_ORDER_FILES_COUNT_LIMIT.toLocaleString()} files or fewer.`;
  const canEditCustomSort = computed(() => !hasTooManyFilesForCustomSort.value);
  const shouldShowCustomSortEdit = computed(() => hasCustomSort.value && isContextOwner.value && collectionDescriptor.value?.context?.files_count > 1);

  const {md: screenMd} = useScreenSize();

  const order = ref(FILE_DEFAULT_SORT_OPTION);

  function setSort(newOrder) {
    order.value = newOrder;
  }

  function hasCondensedIndicator(slot) {
    switch (slot) {
      case 'sort-by':
        return Boolean(order.value?.label);
      case 'asset-type':
        return Boolean(visibleFileTypes.value.some(type => type.active));
      case 'date-range':
        return Boolean(dateRange.value?.displayText);
      case 'people':
        return Boolean(people.value.length);
      case 'albums':
        return Boolean(albums.value.length);
      case 'tags':
        return Boolean(tags.value.length);
      default:
        return false;
    }
  }

  function openCustomSorter() {
    if (!canEditCustomSort.value) {
      useInfoToast().add({
        description: customSortLimitMessage
      });
      return;
    }

    emit('toggle');

    const modal = useFModal({
      id: 'files-custom-sort',
      component: defineAsyncComponent(() => import('./custom-sorter.vue')),
      size: 'fullscreen',
      showClose: false,
      props: {
        album: albumsStore.album
      },
      eventHandlers: {
        done() {
         modal.close();
        }
      }
    });

    modal.open();
  }

  // configure the accordion panels
  const filterPanelItems = computed(() => {
    const filterItems = [{
      label: 'Sort By',
      slot: 'sort-by',
      icon: 'i-ri-sort-desc'
    }];

    if (!props.hideDateFilter && filterPolicy.value.canUseDateFilter) {
      filterItems.push({
        label: order.value?.filterDateLabel,
        slot: 'date-range',
        icon: 'i-ri-calendar-2-line'
      });
    }

    if (shouldShowFileTypeFilter.value) {
      filterItems.splice(1, 0, {
        label: 'Media Type',
        defaultOpen: true,
        slot: 'asset-type',
        icon: 'i-ri-file-text-line'
      });
    }

    if (isContextOwner.value) {
      filterItems.push(
        {
          label: 'People',
          slot: 'people',
          icon: COMMON_ICONS.person
        },
        {
          label: 'Tags',
          slot: 'tags',
          icon: COMMON_ICONS.tag
        },
        {
          label: 'Albums',
          slot: 'albums',
          icon: COMMON_ICONS.album
        }
      );
    }

    return filterItems;
  });

  const accordionConfig = {
    wrapper: 'flex flex-col gap-3',
    container: 'rounded border border-charcoal-300/50 bg-white dark:border-charcoal-600 dark:bg-charcoal-800',
    item: {
      base: 'rounded bg-white text-charcoal dark:bg-charcoal-800 dark:text-gray-100',
      padding: 'p-3 pt-1.5'
    }
  };

  const favoriteActive = ref(false);

  const fileTypes = ref([
    {
      label: 'Photo',
      active: false,
      key: FILE_TYPES.photo
    },
    {
      label: 'Video',
      active: false,
      key: FILE_TYPES.video
    },
    {
      label: 'Audio',
      active: false,
      key: FILE_TYPES.audio
    },
    {
      label: 'Document',
      active: false,
      key: FILE_TYPES.document
    },
    {
      label: 'Project',
      active: false,
      key: FILE_TYPES.project
    }
  ]);

  const visibleFileTypes = computed(() => fileTypes.value.filter(type => (
    (!props.allowedFileTypes || props.allowedFileTypes.includes(type.key))
    && (!filterPolicy.value.allowedFileTypes || filterPolicy.value.allowedFileTypes.includes(type.key))
  )));

  // only offer a choice when there is more than one type to choose from
  const shouldShowFileTypeFilter = computed(() => visibleFileTypes.value.length > 1);

  // when there is no choice, the single allowed type (if restricted) is applied implicitly
  const implicitFileTypes = computed(() => (
    !shouldShowFileTypeFilter.value && props.allowedFileTypes
      ? visibleFileTypes.value.map(type => type.key)
      : []
  ));

  const organizationTypes = ref([
    {
      label: 'Not In Any Album',
      active: false,
      key: FILE_FILTERS.notInAlbum
    },
    {
      label: 'Untagged',
      active: false,
      key: FILE_FILTERS.notTagged
    }
  ]);

  const allowedOrganizationKeys = computed(() => {
    if (filterPolicy.value.isTimelineContext) {
      return isContextOwner.value ? [FILE_FILTERS.notInAlbum, FILE_FILTERS.notTagged] : [];
    }

    const contextType = collectionDescriptor.value?.contextType;

    if (!contextType) {
      return [FILE_FILTERS.notInAlbum, FILE_FILTERS.notTagged];
    }

    if (contextType === FILE_CONTEXTS.album) {
      return [FILE_FILTERS.notTagged];
    }

    if (contextType === FILE_CONTEXTS.tag) {
      return [FILE_FILTERS.notInAlbum];
    }

    if (contextType === FILE_CONTEXTS.person) {
      return [FILE_FILTERS.notInAlbum, FILE_FILTERS.notTagged];
    }

    return [];
  });

  const visibleOrganizationTypes = computed(() =>
    organizationTypes.value.filter(type => allowedOrganizationKeys.value.includes(type.key))
  );

  const shouldShowOrganization = computed(() => visibleOrganizationTypes.value.length > 0);

  const fuzzyDate = useFuzzyDate();
  const dateRange = ref({});

  const tags = ref([]);
  const albums = ref([]);
  const people = ref([]);

  const submitButtonText = computed(() => {
    if (isLoadingFiles.value || isClearing.value) {
      return '';
    } else if (filesCountFallback.value === 0) {
      return 'No Results';
    } else if (effectiveAreFiltersApplied.value) {
      return `Show ${filesCountFallback.value || ''} Results`;
    } else {
      return props.defaultSubmitLabel;
    }
  });

  const dateRangeLabel = computed(() => {
    if (!dateRange.value?.val) {
      return '';
    }

    return dateRange.value.displayText || fuzzyDate.getFormattedString({date: dateRange.value.val[0], type: dateRange.value.type});
  });

  //update internal model from store
  watch(collectionDescriptor, () => {
    favoriteActive.value = Boolean(collectionDescriptor.value?.organization?.includes(FILE_FILTERS.favorites));

    fileTypes.value.forEach(ft => ft.active = Boolean(collectionDescriptor.value?.fileTypes?.includes(ft.key)));
    organizationTypes.value.forEach(ot => ot.active = Boolean(collectionDescriptor.value?.organization?.includes(ot.key)));

    dateRange.value = filterPolicy.value.canUseDateFilter ? (collectionDescriptor.value?.dateRange || {}) : {};
    tags.value = isContextOwner.value ? (collectionDescriptor.value?.tags || []) : [];
    albums.value = isContextOwner.value ? (collectionDescriptor.value?.albums || []) : [];
    people.value = isContextOwner.value ? (collectionDescriptor.value?.people || []) : [];
    const availableSortOptions = sortOptions.value;
    const storedOrder = collectionDescriptor.value?.order;
    order.value = availableSortOptions.find(option => option.paramName === storedOrder?.paramName)
      || availableSortOptions[0]
      || order.value;
  },
  {
    immediate: true,
    deep: true
  });

  // compose date model using a format to be given to the store
  const filtersModel = computed(() => {
    const organization = (() => {
      const o = organizationTypes.value
        .filter(ot => ot.active && allowedOrganizationKeys.value.includes(ot.key))
        .map(ot => ot.key);

      if (favoriteActive.value && isContextOwner.value) {
        o.push(FILE_FILTERS.favorites);
      }

      return o;
    })();

    return {
      fileTypes: shouldShowFileTypeFilter.value
        ? visibleFileTypes.value.filter(ft => ft.active).map(ft => ft.key)
        : implicitFileTypes.value,
      organization,

      dateRange: filterPolicy.value.canUseDateFilter ? dateRange.value : null,
      tags: isContextOwner.value ? tags.value : [],
      albums: isContextOwner.value ? albums.value : [],
      people: isContextOwner.value ? people.value : [],
      order: order.value
    };
  });

  //todo: remove
  //watch(filtersModel, newVal => console.log('filtersModel', newVal), {immediate: true});

  const isClearing = ref(false);

  function getClearedCollectionDescriptor() {
    return {
      ...collectionDescriptor.value,
      albums: [],
      tags: [],
      people: [],
      fileTypes: implicitFileTypes.value,
      organization: [],
      dateRange: null
    };
  }

  async function clear() {
    isClearing.value = true;

    try {
      if (implicitFileTypes.value.length) {
        await filesStore.getFiles({
          ...getClearedCollectionDescriptor(),
          storeCollection: storeFiltersState
        });
      } else {
        await filesStore.clearFilters({storeCollection: storeFiltersState});
      }
    } finally {
      isClearing.value = false;
    }
  }

  async function filter() {
    const newCollectionDescriptor = {
      ...collectionDescriptor.value,
      ...filtersModel.value
    };

    if (isEqual(newCollectionDescriptor, collectionDescriptor.value)) {
      return;
    }

    try {
      await filesStore.getFiles({
        ...newCollectionDescriptor,
        storeCollection: storeFiltersState
      });
    } catch (e) {
      useErrorToast().add();
    }
  }

  watch(filtersModel, filter);
</script>
